import React, { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Paper,
  InputAdornment,
  PaperProps,
  GridProps,
  useTheme,
} from "@mui/material";
import Autocomplete, {
  autocompleteClasses,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { SearchIcon } from "../../Icons";
import useDebounce from "~hooks/useDebounce";
import { getProductSearch } from "~api/search.api";
import { Product as IProduct } from "~api/stream/interfaces";
import { useProductsContext } from "~contexts/Products";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { ProductCard } from "~components/atoms";
import ProductDetailModal from "~components/molecules/ProductDetailModal";

interface ProductSearchProps {
  disabled?: boolean;
}

const ProductSearch = ({ disabled = false }: ProductSearchProps) => {
  const theme: any = useTheme();
  const { currentStream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();
  const {
    setProductList,
    buttonActive,
    buttons,
    getCategoryProductsFromCache,
    addProductToCategoryCache,
  } = useProductsContext();
  const [searchResults, setSearchResults] = useState<IProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState<boolean>(false);
  const [productModalOpen, setProductModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const debounceSearchTerm = useDebounce(searchTerm);
  const isSearchingRef = React.useRef<boolean>(false);
  const lastSearchedTermRef = React.useRef<string>("");

  const customStack = (props: PaperProps) => {
    return <Paper variant="autocompletePaperGrid" {...props} />;
  };

  const customListbox = (props: GridProps) => {
    return <Grid container flexDirection="row" {...props} />;
  };

  // Resetar busca ao trocar de categoria
  useEffect(() => {
    setSearchTerm("");
    setSearchResults([]);
    setOpen(false);
  }, [buttonActive]);

  // Buscar na VTEX apenas se não encontrar na listagem filtrada e for um ID completo
  useEffect(() => {
    if (disabled) {
      setSearchTerm("");
      setSearchResults([]);
      setOpen(false);
      return;
    }
    if (!debounceSearchTerm || debounceSearchTerm.length < 3 || !currentStream) {
      setSearchResults([]);
      setOpen(false);
      isSearchingRef.current = false;
      lastSearchedTermRef.current = "";
      return;
    }

    const formatDebounceSearchTerm = debounceSearchTerm
      .normalize("NFD")
      .replace(/([\u0300-\u036f]|[^0-9a-zA-Z])/g, "");

    // VTEX search restriction removed as per user request
    // if (!/^\d+$/.test(formatDebounceSearchTerm) || formatDebounceSearchTerm.length < 6) { ... }

    // Evitar buscar o mesmo termo múltiplas vezes
    if (lastSearchedTermRef.current === formatDebounceSearchTerm) {
      return;
    }

    const activeButton = buttons[buttonActive];
    const categoryId = activeButton?.id_button;

    if (!categoryId) {
      setSearchResults([]);
      setOpen(false);
      isSearchingRef.current = false;
      return;
    }

    // Evitar múltiplas requisições simultâneas
    if (isSearchingRef.current) {
      return;
    }

    // Verificar se há produtos no cache da categoria atual
    const normalizedSearch = formatDebounceSearchTerm.toLowerCase();
    const cachedProducts = getCategoryProductsFromCache(categoryId);

    // Verificar se algum produto no cache tem o ID exato
    const foundInCache = cachedProducts.some((product) => {
      const id = String(product.provider?.productId || product.id || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/([\u0300-\u036f]|[^0-9a-zA-Z])/g, "");
      return id === normalizedSearch;
    });

    // Se encontrou no cache, não buscar na VTEX
    if (foundInCache) {
      setSearchResults([]);
      setOpen(false);
      isSearchingRef.current = false;
      lastSearchedTermRef.current = formatDebounceSearchTerm;
      return;
    }

    // Se não encontrou no cache, buscar na VTEX
    isSearchingRef.current = true;
    lastSearchedTermRef.current = formatDebounceSearchTerm;

    try {
      getProductSearch(formatDebounceSearchTerm, currentStream)
        .then((response) => {
          const products = response || [];
          setSearchResults(products);

          // Se encontrou produto na VTEX, abrir modal
          if (products.length > 0 && products[0]) {
            setSelectedProduct(products[0]);
            setProductModalOpen(true);
            setOpen(false); // Fechar autocomplete
          } else {
            setOpen(false);
          }
          isSearchingRef.current = false;
        })
        .catch((error) => {
          console.error("error get product search: ", error);
          setSearchResults([]);
          setOpen(false);
          isSearchingRef.current = false;
        });
    } catch (error) {
      console.error("error get product search: ", error);
      setSearchResults([]);
      setOpen(false);
      isSearchingRef.current = false;
    }
  }, [
    debounceSearchTerm,
    currentStream,
    buttons,
    buttonActive,
    getCategoryProductsFromCache,
    disabled,
  ]);

  // Filtrar a listagem atual de produtos da categoria conforme digitamos (a partir de 3 letras)
  useEffect(() => {
    if (disabled) {
      return;
    }
    const activeButton = buttons[buttonActive];
    const categoryId = activeButton?.id_button;
    if (!categoryId) {
      return;
    }

    const baseList = getCategoryProductsFromCache(categoryId);

    // Para menos de 3 caracteres no input, restaurar imediatamente a lista completa
    if (!searchTerm || searchTerm.length < 3) {
      setProductList(baseList);
      setSearchResults([]);
      setOpen(false);
      return;
    }

    // Aguardar debounce apenas para aplicar o filtro (melhor UX e menos chamadas)
    if (!debounceSearchTerm || debounceSearchTerm.length < 3) {
      return;
    }

    const normalizedSearch = debounceSearchTerm
      .toLowerCase()
      .normalize("NFD")
      .replace(/([\u0300-\u036f]|[^0-9a-zA-Z])/g, "");

    const filtered = baseList.filter((product) => {
      const name = (product.productName || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/([\u0300-\u036f]|[^0-9a-zA-Z])/g, "");

      const id = String(product.provider?.productId || product.id || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/([\u0300-\u036f]|[^0-9a-zA-Z])/g, "");

      return name.includes(normalizedSearch) || id.includes(normalizedSearch);
    });

    setProductList(filtered);
  }, [
    searchTerm,
    debounceSearchTerm,
    buttons,
    buttonActive,
    getCategoryProductsFromCache,
    setProductList,
    disabled,
  ]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: IProduct) =>
      option.productName + (option.provider?.productId || "") + (option.id || ""),
  });

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="flex-end"
      sx={{ mt: theme.spacing(0.5) }}
    >
      <Autocomplete
        noOptionsText={
          !debounceSearchTerm || debounceSearchTerm.length < 3
            ? "Digite ao menos 3 caracteres"
            : "Sem resultados ainda..."
        }
        disabled={disabled}
        options={searchResults || []}
        inputValue={searchTerm}
        forcePopupIcon={false}
        getOptionLabel={(option: IProduct) => option.productName}
        filterOptions={filterOptions}
        PaperComponent={customStack}
        ListboxComponent={customListbox}
        onChange={(e, option, reason) => {
          // Remover funcionalidade de adicionar produto ao clicar no autocomplete
          // O produto será adicionado apenas pelo modal
          if (reason === "selectOption") {
            setSearchTerm("");
          }
        }}
        disablePortal={true}
        clearOnBlur={false}
        open={open && searchResults.length > 0}
        onOpen={() => {
          if (searchTerm.length >= 3 && searchResults.length > 0) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
        }}
        onInputChange={(_, newValue, reason) => {
          setSearchTerm(newValue);
          if (reason === "clear") {
            setSearchResults([]);
            setOpen(false);
          } else if (reason === "input") {
            if (newValue.length < 3) {
              setOpen(false);
              setSearchResults([]);
            }
          }
        }}
        sx={{
          width: "100%",
          [`& .${autocompleteClasses.popupIndicator}`]: {
            transform: "none",
            color: "transparent",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            placeholder="buscar produtos"
            hiddenLabel
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    mr: 0,
                    ml: theme.spacing(1.25),
                    mt: theme.spacing(1),
                    color: "transparent",
                  }}
                >
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& input::placeholder": {
                color: theme.palette.grey[800],
                fontSize: "16px",
              },
            }}
          />
        )}
        renderOption={(props: any, option: IProduct) => {
          return (
            <Grid
              {...props}
              item
              xs={3}
              key={props.key}
              style={{
                mt: theme.spacing(1.25),
                padding: "1px",
              }}
              onClick={(e: React.MouseEvent) => {
                // Remover funcionalidade de clique - produto será adicionado apenas pelo modal
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ProductCard
                productImage={option.images.std[0]}
                productName={option.productName}
                productPrice={`R$ ${option.price.toFixed(2)}`}
                productId={option.id}
                providerId={option.provider?.productId}
              />
            </Grid>
          );
        }}
      />

      <ProductDetailModal
        isOpened={productModalOpen}
        onClose={() => {
          setProductModalOpen(false);
          setSelectedProduct(null);
          setSearchTerm("");
        }}
        product={selectedProduct}
        categoryId={buttons[buttonActive]?.id_button || null}
        onProductAdded={(insertedData) => {
          // Adicionar produto ao cache e à lista atual sem recarregar tudo
          const activeButton = buttons[buttonActive];
          const categoryId = activeButton?.id_button;
          if (categoryId && selectedProduct) {
            // Unir dados da busca (selectedProduct) com o ID retornado pelo backend
            const enrichedProduct = {
              ...selectedProduct,
              id: insertedData.id,
              id_button: insertedData.id_button,
              provider_product_id: insertedData.provider_product_id,
            };
            addProductToCategoryCache(categoryId, enrichedProduct);
            updateStreamSocket({ productsUpdated: true });
          }
        }}
      />
    </Grid>
  );
};

export default ProductSearch;
